import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { pullCloudState, clearCloudLocal, flushCloud, installCloudSync } from '../utils/cloudState';
import { BACKEND_URL } from '../utils/api';

installCloudSync();

const AuthContext = createContext(null);
const USER_KEY = 'bliss_user';
const TOKEN_KEY = 'bliss_access_token';

/* ═══════════════════════════════════════════════════════════════════
   AUTH TOKEN HANDLING — Bearer-token primary, cookie-fallback secondary.

   We keep `withCredentials = true` so legacy cookie sessions still work
   for users mid-session, but every request now ALSO sends the
   `Authorization: Bearer <token>` header read from localStorage. This
   makes auth work in environments where cookies don't carry:
     • WhatsApp / Instagram / Facebook in-app WebViews
     • Some Capacitor APK configurations
     • Cross-origin iframes
   ═══════════════════════════════════════════════════════════════════ */
axios.defaults.withCredentials = true;

export const setAuthToken = (token) => {
  if (token) {
    try { localStorage.setItem(TOKEN_KEY, token); } catch { /* quota */ }
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    try { localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
    delete axios.defaults.headers.common['Authorization'];
  }
};

/* Restore token immediately at module load so the very first /api/auth/me
   call carries the header (otherwise the page boots logged-out). */
try {
  const saved = localStorage.getItem(TOKEN_KEY);
  if (saved) axios.defaults.headers.common['Authorization'] = `Bearer ${saved}`;
} catch { /* localStorage blocked — fall back to cookie */ }

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

/* Tile state lives in localStorage and is mirrored to the cloud (utils/cloudState).
   Scope it to the signed-in account so a second account on the same device starts clean. */
const AURA_OWNER_KEY = 'aura_owner_email';
const LOCAL_ONLY_KEYS = ['aura_gold_top_v1', 'pronoia_gold_today_v1', 'aegis_sealed_protocols_v1', 'codex_alarm_active', 'codex_alarm_started_at'];
const clearAuraLocal = () => {
  clearCloudLocal();
  try { LOCAL_ONLY_KEYS.forEach((k) => localStorage.removeItem(k)); } catch { /* ignore */ }
};
const scopeAuraToUser = (email) => {
  if (!email) return;
  try {
    const owner = localStorage.getItem(AURA_OWNER_KEY);
    if (owner && owner !== email) clearAuraLocal();
    localStorage.setItem(AURA_OWNER_KEY, email);
  } catch { /* ignore */ }
};

// Safe cache helper — NEVER store profile_picture (can be huge base64)
const cacheUser = (data) => {
  scopeAuraToUser(data?.email);
  try {
    sessionStorage.setItem(USER_KEY, JSON.stringify({
      id: data.id,
      email: data.email,
      nickname: data.nickname,
      role: data.role,
      profile_tag: data.profile_tag,
      bliss_id: data.bliss_id,
      life_quote: data.life_quote,
      is_public: data.is_public,
    }));
  } catch {
    // Quota exceeded or other storage error — ignore, state is still valid
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/auth/me`);
      setUser(data);
      cacheUser(data);
      pullCloudState();
    } catch {
      setUser(false);
      sessionStorage.removeItem(USER_KEY);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    const cached = sessionStorage.getItem(USER_KEY);
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        setUser(parsed);
        if (!parsed.role) {
          checkAuth();
          return;
        }
      } catch {
        // ignore parse errors
      }
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${BACKEND_URL}/api/auth/login`, { email, password });
    if (data.access_token) setAuthToken(data.access_token);
    cacheUser(data);
    await pullCloudState();
    setUser(data);
    checkAuth();
    return data;
  };

  const register = async (email, password, nickname, life_quote, profile_tag, bliss_id) => {
    const { data } = await axios.post(`${BACKEND_URL}/api/auth/register`, { email, password, nickname, life_quote, profile_tag, bliss_id });
    if (data.access_token) setAuthToken(data.access_token);
    cacheUser(data);
    await pullCloudState();
    setUser(data);
    return data;
  };

  const logout = async () => {
    await flushCloud();
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {});
    } catch {
      // ignore logout errors
    }
    setAuthToken(null);
    clearAuraLocal();
    try { localStorage.removeItem(AURA_OWNER_KEY); } catch { /* ignore */ }
    localStorage.removeItem('bliss_user');
    sessionStorage.removeItem(USER_KEY);
    setUser(false);
  };

  const updateProfile = async (profileData) => {
    const { data } = await axios.put(`${BACKEND_URL}/api/profile`, profileData);
    setUser(data);
    cacheUser(data);
    return data;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, updateProfile, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};
